import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  Search,
  Download,
  Users,
  ShieldCheck,
  Award,
  Heart,
  Eye,
  X,
  Mail,
  Phone,
  MapPin,
  FileCheck2,
} from "lucide-react";
import { PageHead, Panel, StatCard } from "@/project/components/dashboard-shell";
import { sampleDonors, formatINR } from "@/project/data/sample";
import { downloadCsv } from "@/project/lib/receipt";

export const Route = createFileRoute("/admin/donors")({
  component: AdminDonors,
});

function AdminDonors() {
  const [donorList, setDonorList] = useState(sampleDonors);
  const [searchQuery, setSearchQuery] = useState("");
  const [cityFilter, setCityFilter] = useState("All");
  const [viewingDonor, setViewingDonor] = useState<any | null>(null);

  const cities = ["All", ...new Set(donorList.map((d) => d.city))];

  const filteredDonors = donorList.filter((d) => {
    const matchesSearch =
      d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.city.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCity = cityFilter === "All" || d.city === cityFilter;
    return matchesSearch && matchesCity;
  });

  const totalLifetime = donorList.reduce((s, d) => s + d.total, 0);

  return (
    <>
      <PageHead
        title="Donor Directory & KYC"
        subtitle="Manage registered donors, review 80G PAN verifications, and track lifetime giving rank."
        action={
          <button
            onClick={() =>
              downloadCsv("donor-directory", [
                ["ID", "Name", "City", "Donations Count", "Total Contributed"],
                ...donorList.map((d) => [
                  d.id,
                  d.name,
                  d.city,
                  d.donations,
                  d.total,
                ]),
              ])
            }
            className="flex items-center gap-1.5 rounded-full border border-border bg-white px-4 py-2 text-xs font-semibold hover:bg-muted transition shadow-xs"
          >
            <Download className="h-3.5 w-3.5" /> Export Donors CSV
          </button>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        <StatCard
          label="Registered Donors"
          value={String(donorList.length)}
          tone="primary"
        />
        <StatCard
          label="Total Lifetime Giving"
          value={formatINR(totalLifetime)}
          tone="good"
        />
        <StatCard label="80G Verified PANs" value="100%" tone="warn" />
        <StatCard
          label="Avg Giving / Donor"
          value={formatINR(
            Math.round(totalLifetime / Math.max(1, donorList.length))
          )}
        />
      </div>

      <Panel
        title="Donor Registry"
        action={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 rounded-full border border-border/80 bg-white px-3.5 py-1.5 text-xs w-52 sm:w-64">
              <Search className="h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by name or city..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent outline-none w-full placeholder:text-muted-foreground"
              />
            </div>
            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="rounded-full border border-border/80 bg-white px-3 py-1.5 text-xs font-semibold outline-none cursor-pointer"
            >
              {cities.map((c) => (
                <option key={c} value={c}>
                  {c === "All" ? "All Cities" : c}
                </option>
              ))}
            </select>
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[650px] text-sm">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/30">
              <tr className="text-left">
                <th className="py-3 px-3">Donor Name</th>
                <th className="px-3">City</th>
                <th className="px-3">Gifts Given</th>
                <th className="px-3">80G PAN Status</th>
                <th className="px-3">Total Contributed</th>
                <th className="text-right px-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60 text-xs">
              {filteredDonors.map((d, i) => (
                <tr
                  key={d.id}
                  className="hover:bg-[var(--cream-2)]/60 transition-colors"
                >
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-3">
                      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[var(--maroon)] text-white font-bold text-xs shadow-xs">
                        {d.name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-bold text-[var(--maroon-deep)]">
                          {d.name}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          Rank #{i + 1} Sevak
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 text-muted-foreground font-medium">
                    {d.city}
                  </td>
                  <td className="px-3 font-bold text-foreground">
                    {d.donations} gifts
                  </td>
                  <td className="px-3">
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[10px] font-bold text-emerald-700 border border-emerald-200">
                      <FileCheck2 className="h-3 w-3" /> PAN Verified
                    </span>
                  </td>
                  <td className="px-3 font-extrabold text-[var(--maroon-deep)]">
                    {formatINR(d.total)}
                  </td>
                  <td className="text-right px-3">
                    <button
                      onClick={() => setViewingDonor(d)}
                      className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-white px-3 py-1 text-xs font-bold text-[var(--maroon)] hover:bg-[var(--cream-2)] transition shadow-xs cursor-pointer"
                    >
                      <Eye className="h-3.5 w-3.5" /> View Profile
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {/* ── View Donor Detail Drawer / Modal ───────────────────────────── */}
      {viewingDonor && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-in fade-in"
          onClick={() => setViewingDonor(null)}
        >
          <div
            className="w-full max-w-md rounded-3xl bg-white border border-border p-6 shadow-2xl space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-border pb-3">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-full bg-[var(--maroon)] text-white font-bold text-sm">
                  {viewingDonor.name.charAt(0)}
                </div>
                <div>
                  <h3 className="font-display text-base font-bold text-[var(--maroon-deep)]">
                    {viewingDonor.name}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {viewingDonor.city}, India
                  </p>
                </div>
              </div>
              <button
                onClick={() => setViewingDonor(null)}
                className="grid h-8 w-8 place-items-center rounded-full border border-border hover:bg-muted text-muted-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="rounded-2xl bg-[var(--cream-2)] p-4 space-y-2 border border-border/60 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Lifetime Contributed</span>
                <span className="font-extrabold text-base text-[var(--maroon-deep)]">
                  {formatINR(viewingDonor.total)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Donations</span>
                <span className="font-bold text-foreground">
                  {viewingDonor.donations} gifts recorded
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">PAN Verification</span>
                <span className="font-mono font-bold text-emerald-700">
                  VERIFIED (80G Eligible)
                </span>
              </div>
            </div>

            <div className="pt-2 flex justify-end">
              <button
                onClick={() => setViewingDonor(null)}
                className="rounded-full bg-[var(--maroon)] px-5 py-2 text-xs font-bold text-white hover:bg-[var(--maroon-deep)]"
              >
                Close Profile
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}